import { FC, memo } from 'react';
import { GetBusyGuardReason } from 'src/components/dispatch/resolveBusyGuardReason';
import { ShipmentDetail } from 'src/components/shipment/ShipmentDetail';
import ShipmentOrderLegRunSplitFormPresenter from 'src/components/ShipmentOrderLegRunSplitForm/Presenter';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

import LaneDetailSheet from '../V2ShipmentOrderLanes/presenters/LaneDetailSheet';

type DispatchDetailDialogsProps = {
  /** レーン詳細ドロワー */
  laneSheetId: number | null;
  onCloseLaneSheet: () => void;
  /** 案件編集ダイアログ */
  editShipmentOrderId: number | null;
  onCloseEditShipmentOrder: () => void;
  /** ShipmentDetail が要求する対象日（現行 3330 で渡している） */
  startOn: string;
  /** 伝票分割ダイアログ */
  splitDialogOpen: boolean;
  splitTargetLegRun: ShipmentOrderLegRunForPlanEntity | null;
  onCloseSplitDialog: () => void;
  onSplitSuccess: () => void;
  /**
   * 自動配車の実行中に競合する書き込みを弾く（Task 10 #8）。未指定ならガードしない
   * （カード配車は現状ガードを渡さない）。
   */
  getBusyGuardReason?: GetBusyGuardReason;
  /**
   * UI 層 (disabled/Tooltip) 用の理由。値で受ける（Task 10 #10）。
   * このコンポーネントは `memo` されているため、`getBusyGuardReason`（常に同一関数参照の
   * getter）をそのまま UI 層に使うと isBusy の変化が props の浅い比較に反映されず
   * bail out する（実測で確認済み）。`LaneDetailSheet` の disabled 表示にはこちらを渡す。
   */
  disabledReason?: string;
};

/**
 * 詳細パネル（器 = DetailPanelShell）から開く周辺ダイアログ 3 つをまとめたもの。
 * カード配車・地図配車で共有するため state は持たず、useDispatchDetailActions の state / 閉じる関数を
 * props でそのまま受けて描くだけにしている。
 * 自動配車設定ダイアログ（PlanningPresenter）は通常配車と共有しているため含めない。
 */
const DispatchDetailDialogs: FC<DispatchDetailDialogsProps> = memo(({
  laneSheetId,
  onCloseLaneSheet,
  editShipmentOrderId,
  onCloseEditShipmentOrder,
  startOn,
  splitDialogOpen,
  splitTargetLegRun,
  onCloseSplitDialog,
  onSplitSuccess,
  getBusyGuardReason,
  disabledReason,
}) => (
  <>
    {/* レーン詳細シート（V2ShipmentOrderLanes と同じコンポーネント）
        ConvenienceDispatches のセル編集経由は PM 要望で「基本情報」を最初に開く */}
    <LaneDetailSheet
      laneId={laneSheetId}
      startOn={startOn}
      isOpen={laneSheetId !== null}
      onClose={onCloseLaneSheet}
      initialTab="basicInfo"
      getBusyGuardReason={getBusyGuardReason}
      disabledReason={disabledReason}
    />
    {/* 未割当案件の「案件編集」ダイアログ（/plan/ と同じ ShipmentDetail を再利用） */}
    {editShipmentOrderId != null && (
      <ShipmentDetail
        // 案件 ID が変わったら確実に再マウントして内部状態 (isEditing 等) をリセットする
        key={editShipmentOrderId}
        shipmentId={editShipmentOrderId}
        editShipmentDialogOpen
        onClose={onCloseEditShipmentOrder}
        startOn={startOn}
        editOnOpen
        getBusyGuardReason={getBusyGuardReason}
      />
    )}
    {/* 未割当案件の手動分割ダイアログ */}
    <ShipmentOrderLegRunSplitFormPresenter
      open={splitDialogOpen}
      legRun={splitTargetLegRun}
      onClose={onCloseSplitDialog}
      onSuccess={onSplitSuccess}
      getBusyGuardReason={getBusyGuardReason}
    />
  </>
));

DispatchDetailDialogs.displayName = 'DispatchDetailDialogs';

export default DispatchDetailDialogs;
